<template>
    <wrapper-features-templates-operations
    :showLoader="showSpinner"
    :showOverlayLoader="showOverlaySpinner"
    :applyErrorState="applyErrorState"
    :showFooter="!showSpinner && !isDeleted"
    :customBreadcrumbs="{
        currentArea: t('approvals.operators.delete.breadcrumb')
    }"
    :feedbackOperationData="headerData"
    :footerButtonsParams="buttonsFooterData.filter(el => !el.hidden)"
    :buttonFullWidth="getMobileAppState"
    :tokenTitleAppend="t('approvals.operators.delete.tokenModalTitle')"
    @challengeSuccessHandler="challengeSuccessHandler"
    @challengeErrorHandler="errorHandler($event, deleteHandler)"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <!-- Warning -->
        <infobox-generic
        v-if="!isDeleted && !showSpinner && !applyErrorState"
        state="warning"
        :style="`margin-top: ${getMobileAppState ? '0' : '24'}px`"
        :label="t('approvals.operators.delete.warning')"/>
        <!-- Insert content -->
        <div class="content-wrapper" v-if="!showSpinner && !applyErrorState">
            <infobox-generic
            v-if="isDeleted && headerData.status === 'WARNING'"
            :label="headerData.additionalMessage"/>

            <divider-generic
            icon="person"
            :applyBorder="false"
            :title="isDeleted ? t('approvals.operators.delete.data') : t('approvals.operators.delete.title')"/>

            <!-- Username -->
            <text-block-generic
            :params="{ label: t('approvals.operators.delete.usernameLabel'), content: operatorDetails.username }"/>
            <!-- Name -->
            <text-block-generic
            :params="{ label: t('approvals.operators.delete.nameLabel'), content: operatorDetails.name }"/>
            <!-- Email -->
            <text-block-generic
            :params="{ label: t('approvals.operators.delete.emailLabel'), content: operatorDetails.email }"/>
            <!-- Phone -->
            <text-block-generic
            :params="{ label: t('approvals.operators.delete.phoneLabel'), content: operatorDetails.msisdn }"/>
            <!-- Nif -->
            <text-block-generic
            :params="{ label: t('approvals.operators.delete.nifLabel'), content: operatorDetails.nif || '-' }"/>
            <!-- Profile -->
            <text-block-generic
            :params="{ label: t('approvals.operators.delete.profileLabel'), content: operatorDetails.profile.operatorProfileDescription }"/>
        </div>
    </wrapper-features-templates-operations>
    <!-- Feedback Toast -->
    <toasts-generic
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    :buttonsParams="feedbackToastData.buttons"
    @hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOperations from '@/components/wrappers/featuresTemplates/Operations'
import InfoboxGeneric from '@/components/generic/infobox/Generic'
import DividerGeneric from '@/components/generic/dividers/Generic'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import ToastsGeneric from '@/components/generic/toasts/Generic'

/* Vue & Helpers */
import { ref, computed } from 'vue'
import { navigateByName } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapActions & mapGetters */
const { requestOperatorDetail, deleteOperator } = mapActions(moduleMapper.APPROVALS_OPERATORS)
const { getMobileAppState } = mapGetters()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

const props = defineProps({
    operatorId: Number
})

/* Data */
const operatorDetails = ref(null)
const showSpinner = ref(false)
const showOverlaySpinner = ref(false)
const isDeleted = ref(false)

const buttonsFooterData = computed(() => {
    return [{
        label: t('approvals.operators.delete.cancel'),
        buttonPosition: 'left',
        hidden: getMobileAppState.value,
        callbackFunction: () => { navigateByName(routeList.APPROVALS_OPERATORS_NAME) }
    }, {
        label: t('approvals.operators.delete.validate'),
        buttonType: 'primary',
        icon: 'check-line',
        iconPosition: 'right',
        callbackFunction: () => { deleteHandler() }
    }]
})

const headerData = ref(null)

/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const requestDetails = async () => {
	showSpinner.value = true

	try {
        operatorDetails.value = await requestOperatorDetail(props.operatorId)

        showSpinner.value = false
	} catch (error) {
        if (error.toString() === 'Cancel') return
		showSpinner.value = false
		applyErrorState.value = true
		errorHandler(error.response.data, requestDetails)
	}
}

const deleteHandler = async () => {
    showOverlaySpinner.value = true

    try {
        await deleteOperator(props.operatorId)

        showOverlaySpinner.value = false
	} catch (error) {
        if (error.toString() === 'Cancel') return
		showOverlaySpinner.value = false
		errorHandler(error.response.data, requestDetails)
	}
}

const challengeSuccessHandler = (res) => {
    headerData.value = {
        status: res.errorLevel,
        title: res.message,
        additionalMessage: res.data.additionalMessage ? res.data.additionalMessage : ''
    }

    isDeleted.value = true
}
/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const applyErrorState = ref(false)

const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
	feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}
requestDetails()
</script>

<style lang="scss" scoped>

.content-wrapper {
    width: 100%;

    display: flex;
    flex-direction: column;
    gap: 24px;
}
</style>
